import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import type { AppRouter } from './types';

/**
 * App-wide router hook. Currently backed by Next.js; depend on this hook and
 * `AppRouter` rather than `next/router` directly.
 *
 * The returned object and its methods are referentially stable while the route
 * state is unchanged. Consumers can safely use them as effect/`useCallback`
 * dependencies without triggering re-runs on every render.
 */
export const useAppRouter = (): AppRouter => {
  const router = useRouter();
  const { push: routerPush, replace: routerReplace, back: routerBack } = router;

  const push = useCallback<AppRouter['push']>(
    async (url, options) => {
      return routerPush(url, undefined, options);
    },
    [routerPush],
  );

  const replace = useCallback<AppRouter['replace']>(
    async (url, options) => {
      return routerReplace(url, undefined, options);
    },
    [routerReplace],
  );

  const back = useCallback<AppRouter['back']>(() => {
    routerBack();
  }, [routerBack]);

  return useMemo(
    () => ({
      pathname: router.pathname,
      asPath: router.asPath,
      query: router.query,
      isReady: router.isReady,
      push,
      replace,
      back,
    }),
    [
      router.pathname,
      router.asPath,
      router.query,
      router.isReady,
      push,
      replace,
      back,
    ],
  );
};
